type PanelID = 'rooms-panel' | 'build-panel' | 'interactions-panel' | 'chat-panel' | 'camera-panel' | 'graphics-panel' | 'help-panel' | 'inventory-window' | 'outfits-window' | 'attachment-window' | 'inventory-properties-window';
const panelIDs: PanelID[] = ['rooms-panel', 'build-panel', 'interactions-panel', 'chat-panel', 'camera-panel', 'graphics-panel', 'help-panel', 'inventory-window', 'outfits-window', 'attachment-window', 'inventory-properties-window'];
const el = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

/** Both navigation bars control the same panels, independently of live data rendering. */
export class WorldMenu {
  private requested = new Set<PanelID>(['chat-panel']);
  private available = new Set<PanelID>(['help-panel', 'camera-panel', 'graphics-panel']);
  private triggers = [...document.querySelectorAll<HTMLButtonElement>('[data-panel]')];
  private openers = new Map<PanelID, HTMLElement>();
  private events = new AbortController();
  private invitation = '';

  constructor(private stopInput: () => void, private onInvitation: () => void = () => {}) {
    const options = { signal: this.events.signal };
    window.addEventListener('offworld-open-panel',event=>{const id=(event as CustomEvent<string>).detail;if(panelIDs.includes(id as PanelID))this.open(id as PanelID);},options);
    for (const button of this.triggers) button.addEventListener('click', () => {
      const id = button.dataset.panel as PanelID;
      if (!el(id).hidden) this.close(id);
      else { this.openers.set(id, button); this.open(id); }
    }, options);
    for (const button of document.querySelectorAll<HTMLButtonElement>('[data-close]')) {
      button.addEventListener('click', () => this.close(button.dataset.close as PanelID, true), options);
    }
    for (const id of panelIDs) el(id).addEventListener('pointerdown', () => this.activate(id), options);
    el('assets-shortcut').addEventListener('click', () => el('assets-open').click(), options);
    document.addEventListener('focusin', event => {
      if (event.target instanceof HTMLElement && event.target.closest('button, input, select, textarea, dialog')) this.stopInput();
      const id = panelIDs.find(id => el(id).contains(event.target as Node)); if (id) this.activate(id);
    }, options);
    document.addEventListener('keydown', event => {
      if (event.key !== 'Escape' || event.defaultPrevented || document.querySelector('dialog[open]')) return;
      const focused = panelIDs.find(id => el(id).contains(document.activeElement));
      const last = [...this.requested].reverse().find(id => !el(id).hidden);
      const id = focused ?? last;
      if (id) { event.preventDefault(); this.close(id, true); }
    }, options);
  }

  open(id: PanelID, focus = true): void {
    if (!this.available.has(id)) return;
    this.requested.delete(id); this.requested.add(id);
    this.stopInput(); this.sync(); this.activate(id);
    if (focus) {
      const target = id === 'chat-panel' && !el<HTMLInputElement>('chat-text').disabled
        ? el('chat-text') : el(id).querySelector<HTMLElement>('button, select, input');
      target?.focus();
    }
  }

  private activate(id: PanelID): void {
    if (!this.requested.has(id) || el(id).hidden) return;
    this.requested.delete(id); this.requested.add(id);
    // Preserve the full stacking order, rather than flattening every inactive window.
    [...this.requested].forEach((other, index) => { el(other).style.zIndex = String(index + 10); });
  }

  close(id: PanelID, restoreFocus = false): void {
    this.requested.delete(id); this.sync();
    if (restoreFocus) (this.openers.get(id) ?? this.triggers.find(button => button.dataset.panel === id))?.focus();
  }

  render(signedIn: boolean, connected: boolean, invitation: string): void {
    const incoming = invitation !== '' && invitation !== this.invitation;
    this.invitation = invitation;
    this.available = new Set<PanelID>(['help-panel', 'camera-panel', 'graphics-panel']);
    if (signedIn) this.available.add('rooms-panel');
    if (signedIn && el('inventory-host').childElementCount) this.available.add('inventory-window');
    if(signedIn&&el('outfits-host').childElementCount)this.available.add('outfits-window');
    if(signedIn&&el('attachment-host').childElementCount)this.available.add('attachment-window');
    if(signedIn&&el('inventory-properties-host').childElementCount)this.available.add('inventory-properties-window');
    if (signedIn && connected) for (const id of ['build-panel', 'interactions-panel', 'chat-panel'] as const) this.available.add(id);
    el<HTMLButtonElement>('assets-shortcut').disabled = !signedIn;
    for (const button of this.triggers) button.classList.toggle('has-invitation', !!invitation && button.dataset.panel === 'interactions-panel');
    this.sync();
    if (incoming && this.available.has('interactions-panel')) { this.onInvitation(); this.open('interactions-panel', false); }
  }

  private sync(): void {
    for (const id of panelIDs) {
      const panel = el(id), shown = this.available.has(id) && this.requested.has(id);
      if (!shown && panel.contains(document.activeElement)) el('world').focus();
      panel.hidden = !shown;
      for (const button of this.triggers.filter(button => button.dataset.panel === id)) {
        button.disabled = !this.available.has(id);
        button.setAttribute('aria-expanded', String(shown));
      }
    }
  }

  dispose(): void { this.events.abort(); }
}
